<template>
  <BaseDataTable
    :entries
    :columns
    :loading
    :paginator-info="paginatorInfo"
    row-hover
    data-test="webhook-event-log-table"
    search-disabled
    @search="updateSearchSettings"
    @row-click="openDetails"
  >
    <template #Id="{ row, rowIndex }">
      <div :data-test="`webhook-event-log-id-${rowIndex}`">{{ row.key }}</div>
    </template>
    <template #Webhook="{ row, rowIndex }">
      <div :data-test="`webhook-name-${rowIndex}`">{{ row.webhook.title }}</div>
    </template>
    <template #EventId="{ row, rowIndex }">
      <div :data-test="`webhook-event-id-${rowIndex}`">{{ row.webhookEvent.key }}</div>
    </template>
    <template #Event="{ row, rowIndex }">
      <div :data-test="`webhook-event-description-${rowIndex}`">{{ row.webhookEvent.description }}</div>
    </template>
    <template #Result="{ row, rowIndex }">
      <div :data-test="`webhook-event-log-result-${rowIndex}`">{{ textHelpers.capitalizeFirstLetter(row.result) }}</div>
    </template>
    <template #StatusCode="{ row, rowIndex }">
      <div :data-test="`webhook-event-log-status-code-${rowIndex}`">{{ row.statusCode ?? 'N/A'}}</div>
    </template>
    <template #CreatedAt="{ row, rowIndex }">
      <div :data-test="`webhook-event-log-created-at-${rowIndex}`">{{ row.createdAt }}</div>
    </template>
    <template #Action="{ row }">
      <WdsMenu>
        <template #trigger>
          <WdsButtonIcon transparent-background :data-test="`webhook-event-log-details-open-menu-${row.key}`">
            <WdsIcon name="overflow-menu-horizontal" />
          </WdsButtonIcon>
        </template>
        <WdsMenuOption :data-test="`webhook-event-log-details-${row.key}`" @click="openEventRetry(row)">
          <WdsIcon :size="16" name="send" />
          {{ $t('Retry event') }}
        </WdsMenuOption>
        <WdsMenuOption :data-test="`webhook-event-log-details-${row.key}`" @click="openDetails(row)">
          <WdsIcon :size="16" name="code" />
          {{ $t('Details') }}
        </WdsMenuOption>
      </WdsMenu>
    </template>
  </BaseDataTable>

  <teleport to="#modals">
    <WebhookEventLogDetailsModal v-model:show="showDetails" :details="details" />
    <WebhookEventRetryModal v-model:show="showEventRetry" :details="details" />
  </teleport>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, reactive, ref, Ref } from 'vue'
import { WdsButtonIcon, WdsIcon, WdsMenu, WdsMenuOption } from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { textHelpers } from '@/utils/tools'
import { FragmentType, useFragment } from '@/gql'
import BaseDataTable from '@/components/BaseDataTable'
import { TdDate } from '@/components/BaseDataTable/Cells'
import { WebhookEventLogsQuery } from '@/components/Integrations/Webhooks/graphql/queries'
import { WebhookEventLogFragment } from '@/components/Integrations/Webhooks/graphql/fragments'
import { PaginatorInfoFragment } from '@/components/GraphQL/fragments/paginatorInfoFragment'
import WebhookEventLogDetailsModal from '@/components/Integrations/Webhooks/WebhookEventLogDetailsModal.vue'
import WebhookEventRetryModal from '@/components/Integrations/Webhooks/WebhookEventRetryModal.vue'

const details: Ref<FragmentType<typeof WebhookEventLogFragment> | undefined> = ref(undefined)

const showDetails = ref(false)

const openDetails = (eventLog: FragmentType<typeof WebhookEventLogFragment>) => {
  details.value = eventLog
  showDetails.value = true
}

const showEventRetry = ref(false)

const openEventRetry = (eventLog: FragmentType<typeof WebhookEventLogFragment>) => {
  details.value = eventLog
  showEventRetry.value = true
}

const columns = [
  {
    key: 'key',
    slot: 'Id',
    title: $t('ID'),
    order: 'desc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'webhook.title',
    slot: 'Webhook',
    title: $t('Webhook'),
    order: 'asc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'webhookEvent.event',
    slot: 'Event',
    title: $t('Event'),
    order: 'asc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'webhookEvent.id',
    slot: 'EventId',
    title: $t('Event ID'),
    order: 'asc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'result',
    slot: 'Result',
    title: $t('Result'),
    order: 'asc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'statusCode',
    slot: 'StatusCode',
    title: $t('Status Code'),
    order: 'asc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'createdAt',
    slot: 'CreatedAt',
    title: $t('Sent At'),
    renderUsing: {
      componentProps(row) {
        return {
          date: row.createdAt,
          format: 'Y-MM-DD HH:mm:ss',
        }
      },
      component: TdDate,
    },
    order: 'desc',
    class: 'tw-cursor-pointer',
  },
  {
    key: 'id',
    title: $t('Action'),
    slot: 'Action',
    ignoreClickForRowSelection: true,
  },
]

const pagination = reactive({
  page: 1,
  first: 50,
})

interface BaseDataTableSearchSettings {
  page: number
  perPage: number
  search: string
}

const updateSearchSettings = (searchSettings: BaseDataTableSearchSettings) => {
  pagination.page = searchSettings.page + 1
  pagination.first = searchSettings.perPage
}

const clientId = 'publicApi'

const { result, loading } = useQuery(
  WebhookEventLogsQuery,
  () => ({
    accounts: [currentAccount.globalId],
    first: pagination.first,
    page: pagination.page,
  }),
  { clientId },
)

const entries = computed(() => {
  if (loading.value) {
    return []
  }

  return result.value?.webhookEventLogs.data.map((hook) => useFragment(WebhookEventLogFragment, hook))
})

const paginatorInfo = computed(
  () => useFragment(PaginatorInfoFragment, result.value?.webhookEventLogs.paginatorInfo) ?? undefined,
)

</script>

<style scoped lang="scss"></style>
